import React from 'react';
import { CheckCircle, AlertTriangle, Lightbulb } from 'lucide-react';

/**
 * Generate agent insights from analysis data
 * Provides actionable strengths, watch points, and opportunities
 */
function generateAgentInsights(result) {
    const strengths = [];
    const watchPoints = [];
    const opportunities = [];

    const { priceEfficiency, locationContext, listingQuality, spaceConfiguration } = result;

    // STRENGTHS - What's good about this listing
    if (priceEfficiency?.pricePerM2 && priceEfficiency.pricePerM2 < 3000) {
        strengths.push('Competitive pricing for the area');
    }
    if (priceEfficiency?.score >= 70) {
        strengths.push('Strong value proposition for buyers');
    }
    if (locationContext?.score >= 70) {
        strengths.push(`Prime ${locationContext.neighborhood || 'location'}`);
    }
    if (locationContext?.transportScore >= 75) {
        strengths.push('Excellent transport accessibility');
    }
    if (listingQuality?.photoCount >= 10) {
        strengths.push('Comprehensive photo coverage for marketing');
    }
    if (listingQuality?.score >= 70) {
        strengths.push('Well-presented, professional listing');
    }
    if (spaceConfiguration?.spaceRating === 'spacious' || spaceConfiguration?.spaceRating === 'good') {
        strengths.push('Excellent space configuration');
    }
    if (spaceConfiguration?.bathroomRating === 'excellent' || spaceConfiguration?.bathroomRating === 'very good') {
        strengths.push('Strong bathroom-to-bedroom ratio');
    }

    // WATCH POINTS - Things to verify/investigate
    if (!listingQuality?.photoCount || listingQuality.photoCount < 5) {
        watchPoints.push('Limited photos - schedule property viewing before presenting');
    }
    if (priceEfficiency?.pricePerM2 && priceEfficiency.pricePerM2 > 5000) {
        watchPoints.push('Premium pricing - verify with comparable properties');
    }
    if (priceEfficiency?.pricePerM2 && priceEfficiency.pricePerM2 < 1000) {
        watchPoints.push('Unusually low pricing - verify data accuracy');
    }
    if (spaceConfiguration?.bathroomRatio && spaceConfiguration.bathroomRatio < 0.5) {
        watchPoints.push('Limited bathrooms - may affect buyer appeal');
    }
    if (listingQuality?.score < 50) {
        watchPoints.push('Incomplete listing information - request additional details');
    }
    if (locationContext?.score < 50) {
        watchPoints.push('Location data limited - verify neighborhood amenities');
    }
    if (listingQuality?.missing && listingQuality.missing.length > 3) {
        watchPoints.push(`Missing ${listingQuality.missing.length} data points - request from seller`);
    }

    // OPPORTUNITIES - How to position/sell this property
    if (priceEfficiency?.pricePerM2 && priceEfficiency.pricePerM2 < 2000) {
        opportunities.push('Below-market pricing - strong value opportunity for investors');
    }
    if (locationContext?.transportScore >= 75) {
        opportunities.push('Highlight transport access for rental demand');
    }
    if (locationContext?.amenities && locationContext.amenities.length > 0) {
        opportunities.push(`Market ${locationContext.amenities.length}+ local amenities`);
    }
    if (priceEfficiency?.score >= 70 && locationContext?.score >= 70) {
        opportunities.push('Strong fundamentals - ideal for portfolio diversification');
    }
    if (spaceConfiguration?.spaceRating === 'spacious') {
        opportunities.push('Emphasize spacious layout for family market');
    }
    if (result.opportunities && result.opportunities.length > 0) {
        // Add existing opportunities from analysis
        opportunities.push(...result.opportunities.slice(0, 2));
    }

    // Ensure we always have some content
    if (strengths.length === 0) strengths.push('Standard market listing - verify details');
    if (watchPoints.length === 0) watchPoints.push('Conduct thorough property inspection');
    if (opportunities.length === 0) opportunities.push('Suitable for standard portfolio inclusion');

    return {
        strengths: strengths.slice(0, 4),
        watchPoints: watchPoints.slice(0, 4),
        opportunities: opportunities.slice(0, 4)
    };
}

/**
 * Agent Insights Section - Actionable intelligence for real estate agents
 * Organized into Strengths, Watch Points, and Opportunities
 */
function AgentInsightsSection({ result }) {
    const insights = generateAgentInsights(result);

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-5">
                <div className="p-2 bg-gradient-to-br from-primary-500 to-primary-600 rounded-lg shadow-sm">
                    <Lightbulb className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">Agent Insights</h3>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
                {/* Strengths */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-5 border border-green-200">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-green-100 rounded-lg">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <h4 className="font-semibold text-slate-900">Strengths</h4>
                    </div>
                    {insights.strengths.length > 0 ? (
                        <ul className="space-y-2">
                            {insights.strengths.map((item, idx) => (
                                <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                                    <span className="text-green-500 mt-0.5 flex-shrink-0 font-bold">✓</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-slate-400 italic">Request more property details</p>
                    )}
                </div>

                {/* Watch Points */}
                <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-lg p-5 border border-yellow-200">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-yellow-100 rounded-lg">
                            <AlertTriangle className="w-5 h-5 text-yellow-600" />
                        </div>
                        <h4 className="font-semibold text-slate-900">Watch Points</h4>
                    </div>
                    {insights.watchPoints.length > 0 ? (
                        <ul className="space-y-2">
                            {insights.watchPoints.map((item, idx) => (
                                <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                                    <span className="text-yellow-500 mt-0.5 flex-shrink-0 font-bold">⚠</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-slate-400 italic">No major concerns identified</p>
                    )}
                </div>

                {/* Opportunities */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-5 border border-blue-200">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="p-1.5 bg-blue-100 rounded-lg">
                            <Lightbulb className="w-5 h-5 text-blue-600" />
                        </div>
                        <h4 className="font-semibold text-slate-900">Opportunities</h4>
                    </div>
                    {insights.opportunities.length > 0 ? (
                        <ul className="space-y-2">
                            {insights.opportunities.map((item, idx) => (
                                <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                                    <span className="text-blue-500 mt-0.5 flex-shrink-0 font-bold">→</span>
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="text-sm text-slate-400 italic">Standard market opportunity</p>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AgentInsightsSection;
